import { NotePageType } from './types'

/** The fields this decision reads. Kept loose so it can be called with a raw API row. */
export interface NoteOnPageCandidate {
  type?: string | null
  notePageType?: string | null
}

/**
 * Whether a note belongs on one of the loan's three notes screens.
 *
 * **Notes is the whole loan's list; Servicing Notes and REO Notes are the two filtered ones.** A
 * note filed from Servicing Notes or REO Notes also shows under Notes, so Notes is the one place
 * that answers "everything said about this loan". The reverse does not hold: a note filed from
 * Notes stays off the two specialised screens.
 *
 * This has now been both ways round, so read the history before changing it again.
 * Originally the global screen let every note through. That was changed to strict equality after
 * Longhorn reported Notes and Servicing Notes showing the same thing - which they did, because on a
 * serviced loan nearly every note is written from Servicing Notes, so the two menu entries listed
 * the same rows and the separation looked like it did not exist. Longhorn then asked for the
 * superset back (2026-10-14), on the reasoning that Notes should mean all notes and Servicing
 * Notes just the servicing ones. That is the rule above.
 *
 * **The known cost, accepted deliberately:** on a heavily serviced loan Notes will again look much
 * like Servicing Notes, because that is where the notes were written - including the 303,518
 * conversation rows the TMO conversion files as `servicing`. If that duplication is reported as a
 * bug again, the fix is not to restore strict equality here; it is to decide which screen a
 * conversation log belongs on.
 *
 * An email note shows on all three screens. Mail to loan-<n>@reply files once, through
 * NoteService.createNoteFromReply, which stores it as a global note, and servicing works out of
 * Servicing Notes; re-routing the filing instead would have moved borrower replies off Notes for
 * everyone else and stranded the ones already filed. So email is the one shared conversation
 * stream running across the lists.
 *
 * A note stored before notePageType existed counts as global, so it keeps a screen rather than
 * matching none and disappearing. The isServicing backfill in the backend's patch.service.ts gave
 * the real rows a value, so this only guards a null.
 *
 * Nothing here moves or hides a note on the server: GET servicing/note/:loanNumber takes no page
 * type and returns every note for the loan, so this decides only what each screen lists.
 */
export function belongsOnPage(item: NoteOnPageCandidate, notePageType: NotePageType): boolean {
  if (item?.type === 'email') return true
  // Notes lists the loan's whole set; only the specialised screens filter.
  if (notePageType === NotePageType.GLOBAL) return true
  return (item?.notePageType || NotePageType.GLOBAL) === notePageType
}
